import {
  CalendarDays,
  CheckSquare,
  Eye,
  Home,
  LogOut,
  Menu,
  Plus,
  Settings,
  Shield,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface HeaderProps {
  onNewProject: () => void;
  onViewProjects: () => void;
}

export function Header({ onNewProject, onViewProjects }: HeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toast } = useToast();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
      toast({ title: "Logged out successfully" });
    } catch (error) {
      toast({ variant: "destructive", title: "Logout failed" });
    }
    setIsOpen(false);
  };

  const navigationItems = [
    {
      icon: Home,
      label: "Dashboard",
      action: () => {
        navigate("/dashboard");
        setIsOpen(false);
      },
    },
    {
      icon: Plus,
      label: "New Project",
      action: () => {
        onNewProject();
        setIsOpen(false);
      },
    },
    {
      icon: Eye,
      label: "View All Projects",
      action: () => {
        onViewProjects();
        setIsOpen(false);
      },
    },
    {
      icon: Users,
      label: "Customer Database",
      action: () => {
        navigate("/customers");
        setIsOpen(false);
      },
    },
    {
      icon: CalendarDays,
      label: "Calendar",
      action: () => {
        navigate("/meetings/calendar");
        setIsOpen(false);
      },
    },
    // Admin-only: Checklist Management
    ...(user?.role === "admin"
      ? [
          {
            icon: CheckSquare,
            label: "Checklist Management",
            action: () => {
              navigate("/checklist-management");
              setIsOpen(false);
            },
          },
          {
            icon: Shield,
            label: "User Management",
            action: () => {
              navigate("/user-management");
              setIsOpen(false);
            },
          },
        ]
      : []),
  ];

  const settingsItem = {
    icon: Settings,
    label: "Settings",
    action: () => {
      navigate("/settings");
      setIsOpen(false);
    },
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center justify-between px-4">
        <div className="flex items-center space-x-2">
          <h1 className="text-xl font-semibold">Reno Quote App</h1>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-2">
          {navigationItems.map((item) => (
            <Button
              key={item.label}
              variant="ghost"
              size="sm"
              onClick={item.action}
              className="flex items-center space-x-2"
            >
              <item.icon className="h-4 w-4" />
              <span>{item.label}</span>
            </Button>
          ))}
          {/* Settings and Logout */}
          <Button
            variant="ghost"
            size="sm"
            onClick={settingsItem.action}
            className="ml-2"
            aria-label="Settings"
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} aria-label="Logout">
            <LogOut className="h-4 w-4" />
          </Button>
        </nav>

        {/* Mobile Navigation */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild={true}>
            <Button variant="ghost" size="sm" className="md:hidden" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px] sm:w-[400px]">
            <SheetHeader>
              <SheetTitle>Navigation Menu</SheetTitle>
            </SheetHeader>
            <nav className="flex flex-col space-y-4 mt-6">
              {navigationItems.map((item) => (
                <Button
                  key={item.label}
                  variant="ghost"
                  size="lg"
                  onClick={item.action}
                  className="flex items-center justify-start space-x-3 h-12"
                >
                  <item.icon className="h-5 w-5" />
                  <span className="text-base">{item.label}</span>
                </Button>
              ))}
              {/* Settings in mobile menu with label */}
              <Button
                variant="ghost"
                size="lg"
                onClick={settingsItem.action}
                className="flex items-center justify-start space-x-3 h-12"
              >
                <settingsItem.icon className="h-5 w-5" />
                <span className="text-base">{settingsItem.label}</span>
              </Button>
              {/* Logout in mobile menu */}
              <Button
                variant="ghost"
                size="lg"
                onClick={handleLogout}
                className="flex items-center justify-start space-x-3 h-12 text-destructive"
              >
                <LogOut className="h-5 w-5" />
                <span className="text-base">Logout</span>
              </Button>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
